import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Check, IndianRupee, MessageSquare, X } from 'lucide-react';
import { SEO } from '../components/common/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { UPI_ID, PAYEE_NAME, WHATSAPP_NUMBER, QR_IMAGE_URL } from '../data/constants';

export const SupportPage = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  }, []);

  const supportTiers = [
    { emoji: '☕', amount: 99, title: 'Starter Support', description: 'Support a small idea', color: 'bg-amber-50 text-amber-700 border-amber-200', hoverColor: 'hover:border-amber-400' },
    { emoji: '⭐', amount: 299, title: 'Support Local', badge: 'Most Popular', description: 'Help local businesses grow', color: 'bg-orange-50 text-orange-700 border-orange-300', hoverColor: 'hover:border-orange-500', featured: true },
    { emoji: '🚀', amount: 499, title: 'Growth Support', description: 'Expand reach to more shops', color: 'bg-blue-50 text-blue-700 border-blue-200', hoverColor: 'hover:border-blue-400' },
    { emoji: '🌟', amount: 999, title: 'Super Support', description: 'Make real impact in local economy', color: 'bg-purple-50 text-purple-700 border-purple-200', hoverColor: 'hover:border-purple-400' }
  ];

  const generateUPILink = (amount) => {
    return `upi://pay?pa=${UPI_ID}&pn=${encodeURIComponent(PAYEE_NAME)}&am=${amount}&cu=INR&tn=${encodeURIComponent('JustOffs Support')}`;
  };

  const handlePayment = (tier) => {
    setSelectedPlan(tier);
    
    if (isMobile) {
      toast.info('Redirecting to UPI app...');
      const upiLink = generateUPILink(tier.amount);
      window.location.href = upiLink;
      
      setTimeout(() => {
        setPaymentInitiated(true);
        toast.success('Complete payment in your UPI app');
      }, 1500);
    } else {
      setShowQR(true);
    }
  };

  const handleWhatsAppConfirm = () => {
    const message = encodeURIComponent(`Hi! I have completed payment of ₹${selectedPlan?.amount} for JustOffs ${selectedPlan?.title}. Please confirm my support.`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="support-page">
      <SEO title="Support JustOffs - Help Local Businesses Grow" description="Support JustOffs and help local businesses in India grow. MSMEs contribute 30% to India's GDP. Together, let's support the real economy." />
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-10">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2 text-center">Support Local. Support <span className="text-orange-500">Real India</span>.</h1>
        <p className="text-slate-500 text-center mb-4">Help us grow and support local businesses</p>
        
        {/* Urgency Banner */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl p-4 mb-8 text-center">
          <p className="font-semibold flex items-center justify-center gap-2">
            <span className="text-2xl">🚀</span> Be among the first 100 supporters!
          </p>
        </div>

        {/* Support Plans */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {supportTiers.map((tier, idx) => (
            <div key={idx} className={`p-5 rounded-2xl border-2 ${tier.color} ${tier.hoverColor} relative transition-all duration-300 hover:shadow-lg ${tier.featured ? 'sm:scale-105 shadow-md' : ''}`} data-testid={`support-tier-${tier.amount}`}>
              {tier.badge && <span className="absolute -top-3 right-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs px-3 py-1 rounded-full font-medium shadow-sm">{tier.badge}</span>}
              <div className="flex items-start gap-3 mb-4">
                <span className="text-3xl">{tier.emoji}</span>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold">₹{tier.amount}</span>
                  </div>
                  <span className="font-semibold text-slate-800">{tier.title}</span>
                  <p className="text-sm mt-1 opacity-80">{tier.description}</p>
                </div>
              </div>
              <button onClick={() => handlePayment(tier)} className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${tier.featured ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 shadow-md' : 'bg-white text-slate-700 border border-slate-200 hover:border-orange-400 hover:text-orange-600'}`} data-testid={`pay-${tier.amount}-btn`}>
                <IndianRupee className="w-4 h-4" /> Contribute via UPI
              </button>
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="flex flex-wrap justify-center gap-4 mb-8 text-sm text-slate-600">
          <span className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full border border-slate-200">
            <Check className="w-4 h-4 text-green-500" /> Secure UPI Payment
          </span>
          <span className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full border border-slate-200">
            <Check className="w-4 h-4 text-green-500" /> No hidden charges
          </span>
          <span className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full border border-slate-200">
            <Check className="w-4 h-4 text-green-500" /> Direct support to JustOffs
          </span>
        </div>

        {/* Payment Initiated - WhatsApp Confirmation */}
        {paymentInitiated && selectedPlan && (
          <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6 mb-8 text-center animate-pulse">
            <div className="text-4xl mb-3">✅</div>
            <h3 className="text-lg font-bold text-green-800 mb-2">Payment Initiated for ₹{selectedPlan.amount}</h3>
            <p className="text-green-700 text-sm mb-4">Complete payment in your UPI app, then confirm below</p>
            <button onClick={handleWhatsAppConfirm} className="bg-green-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-600 transition flex items-center justify-center gap-2 mx-auto" data-testid="whatsapp-confirm-btn">
              <MessageSquare className="w-5 h-5" /> Confirm Payment on WhatsApp
            </button>
          </div>
        )}

        {/* Did you know section */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
          <h3 className="text-lg font-bold text-slate-800 mb-4">Did you know?</h3>
          <div className="space-y-4 text-slate-600 text-sm">
            <p>Micro, Small and Medium Enterprises (MSMEs) contribute <strong>~30% to India's GDP</strong> and account for over <strong>45% of exports</strong>. They also employ <strong>110+ million people</strong>, making them one of the largest employment generators in the country.</p>
            <p>Yet, many local businesses and MSMEs continue to struggle with:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Limited online visibility</li>
              <li>Rising competition from large e-commerce platforms</li>
              <li>Difficulty reaching nearby customers</li>
            </ul>
            <p>We believe the real strength of India lies in its local markets, street vendors, and small business owners.</p>
            <p>With this vision, we're building <strong>JustOffs</strong> — a free classified platform aimed at helping local businesses connect with nearby customers and improve offline shopping discovery.</p>
          </div>
          <div className="bg-slate-100 rounded-xl p-5 mt-4 mb-4">
            <h4 className="text-base font-semibold text-slate-800 mb-3">Our goal is simple:</h4>
            <ul className="space-y-2 text-slate-600 text-sm">
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-slate-700 mt-0.5" /> Make local businesses more visible</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-slate-700 mt-0.5" /> Help customers discover nearby deals easily</li>
              <li className="flex items-start gap-2"><Check className="w-4 h-4 text-slate-700 mt-0.5" /> Support the growth of India's real economy</li>
            </ul>
          </div>
          <p className="text-base text-slate-700 font-medium">If you know any business owners, shopkeepers, or local sellers — feel free to share this with them and help them get started with <span className="text-slate-600 font-semibold">#JustOffs</span>.</p>
        </div>
        
        <div className="text-center">
          <p className="text-lg font-semibold text-slate-800 mb-4">JustOffs – Discover Local. Support Local. Grow Together.</p>
          <Link to="/post-ad" className="inline-flex items-center gap-2 bg-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-600 transition" data-testid="post-ad-btn"><Plus className="w-5 h-5" /> Post Free Ad</Link>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQR && selectedPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowQR(false)} data-testid="qr-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowQR(false)} className="absolute top-4 right-4 p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Pay ₹{selectedPlan.amount}</h3>
            <p className="text-slate-500 text-sm mb-4">{selectedPlan.title}</p>
            
            <div className="bg-slate-50 rounded-xl p-4 mb-4">
              <img src={QR_IMAGE_URL} alt="UPI QR Code" className="w-64 h-64 mx-auto rounded-lg" />
            </div>
            
            <div className="text-sm text-slate-600 mb-4">
              <p className="font-medium">UPI ID: {UPI_ID}</p>
              <p>Payee: {PAYEE_NAME}</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <p className="text-amber-700 text-sm">📱 Scan with any UPI app (PhonePe, GPay, Paytm)</p>
            </div>
            
            <button onClick={() => { setShowQR(false); setPaymentInitiated(true); }} className="w-full bg-green-500 text-white py-3 rounded-xl font-semibold hover:bg-green-600 transition flex items-center justify-center gap-2">
              <Check className="w-5 h-5" /> I've Completed Payment
            </button>
            
            <button onClick={handleWhatsAppConfirm} className="w-full mt-3 bg-white text-green-600 border border-green-200 py-3 rounded-xl font-semibold hover:bg-green-50 transition flex items-center justify-center gap-2">
              <MessageSquare className="w-5 h-5" /> Confirm on WhatsApp
            </button>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};
